import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { UseFormReturn } from 'react-hook-form';
import { useApptekster } from '../../contexts/ApptekstContext';
import { UseApiProps } from '../../hooks/useApi';
import { useFeil } from '../../hooks/useFeil';
import { captchaSiteKey } from '../../utils/captcha';
import { TegnkombinasjonStatus } from '../../utils/api';

import './TegnkombinasjonSkjema.scss';
import { PakrevdFelt } from '../PakrevdFelt';
import { SVVSearchField } from '@komponentkassen/svv-form';

export type TegnkombinasjonSkjemaData = { sokefelt: string };

type TegnkombinasjonSkjemaProps = {
    api: UseApiProps<TegnkombinasjonStatus>;
    skjema: UseFormReturn<TegnkombinasjonSkjemaData>;
    className?: string;
    tegnkombinasjon?: string;
    variant?: 'apen' | 'innlogget';
    onSubmit?: (tegnkombinasjon: string) => void;
};

export const TegnkombinasjonSkjema: React.FC<TegnkombinasjonSkjemaProps> = ({
    className,
    api: { hent, data, timestamp },
    tegnkombinasjon: videresendtTegnkombinasjon = '',
    variant = 'apen',
    onSubmit,
}) => {
    const t = useApptekster();
    const [feilmelding, setFeilmelding] = useState('');

    const { tilFeilmelding } = useFeil({
        reservert:
            'personlig-kjennemerke.soknad.feilmelding.tegnkombinasjon.reservert',
        opptatt:
            'personlig-kjennemerke.soknad.feilmelding.tegnkombinasjon.opptatt',
        mangler_kjoretoy:
            'personlig-kjennemerke.soknad.validering.mangler-kjoretoy',
        kan_ikke_soke: 'personlig-kjennemerke.soknad.validering.kan-ikke-soke',
        for_mange_soknader:
            'personlig-kjennemerke.soknad.validering.for-mange-soknader',
        default:
            'personlig-kjennemerke.soknad.feilmelding.tegnkombinasjon.ugyldig',
    });

    const [tegnkombinasjon, setTegnkombinasjon] = useState<string>(
        videresendtTegnkombinasjon,
    );
    const [captchaToken, setCaptchaToken] = useState<string>();

    const visCaptcha = data?.captchaVis && !data?.captchaVerifisert;

    useEffect(() => {
        if (tegnkombinasjon) {
            hent(tegnkombinasjon, captchaToken);
        }
    }, [hent, captchaToken, tegnkombinasjon]);

    useEffect(() => {
        if (timestamp && data?.ok === false && !visCaptcha) {
            setFeilmelding(tilFeilmelding(data.melding));
        }
    }, [data, timestamp, tilFeilmelding, visCaptcha]);

    return (
        <div className={className}>
            <PakrevdFelt className="spacing-bottom--small" />
            <SVVSearchField
                label={
                    variant === 'apen'
                        ? t(
                              'personlig-kjennemerke.apne-sider.tegnkombinasjon.label',
                          )
                        : t(
                              'personlig-kjennemerke.soknad.tegnkombinasjon.label',
                          )
                }
                placeholder={t(
                    'personlig-kjennemerke.soknad.tegnkombinasjon.placeholder',
                )}
                maxLength={7}
                errorMessage={feilmelding}
                id="sok-personlig-skilt"
                onSearch={(e) => {
                    if (e.length === 0) {
                        setFeilmelding(
                            t(
                                'personlig-kjennemerke.soknad.tegnkombinasjon.validering.paakrevd',
                            ),
                        );
                    } else if (e.length < 2) {
                        setFeilmelding(
                            t(
                                'personlig-kjennemerke.soknad.tegnkombinasjon.validering.for-kort',
                            ),
                        );
                    } else if (!e.match(/^[a-zA-ZæøåÆØÅ0-9 ]*$/)) {
                        setFeilmelding(
                            t(
                                'personlig-kjennemerke.soknad.tegnkombinasjon.validering.ugyldig',
                            ),
                        );
                    } else {
                        setFeilmelding('');
                        setTegnkombinasjon(e.toUpperCase());
                        if (onSubmit) onSubmit(e.toUpperCase());
                    }
                }}
                required
            />
            {visCaptcha && (
                <ReCAPTCHA
                    sitekey={captchaSiteKey()}
                    onChange={(token) => token && setCaptchaToken(token)}
                />
            )}
        </div>
    );
};
